import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { CRM_VISIT_STATUS } from "../../lib/constants";
import { enumLabel } from "../../lib/crmLabels";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import {
  CalendarClock, Plus, Pencil, Trash2, ListChecks, ArrowUp, ArrowDown,
  CheckCircle2, Clock, GripVertical, Building2, Mail, Send,
} from "lucide-react";

const emptyVisit = {
  prospect_id: "", prospect_name: "", visit_date: "", visit_time: "",
  status: "planifiee", conducted_by: "", checklist: [], notes: "", next_step: "", next_followup: "",
};

export default function VisitsTab({ prospects, onChange, readOnly }) {
  const { t } = useTranslation();
  const [visits, setVisits] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [open, setOpen] = useState(false);
  const [clOpen, setClOpen] = useState(false);
  const [form, setForm] = useState(emptyVisit);
  const [edit, setEdit] = useState(null);
  const [newItem, setNewItem] = useState("");
  const [settings, setSettings] = useState({});
  const [dirDlg, setDirDlg] = useState(false);
  const [famDlg, setFamDlg] = useState(false);
  const [target, setTarget] = useState(null);
  const [dirForm, setDirForm] = useState({ visit_date: "", visit_time: "", recommendations: "", attache_email: "" });
  const [famForm, setFamForm] = useState({ visit_date: "", visit_time: "", address: "", cancel_phone: "", cancel_email: "" });
  const [sendingDir, setSendingDir] = useState(false);
  const [sendingFam, setSendingFam] = useState(false);
  const [recoTemplates, setRecoTemplates] = useState([]);
  const [logDlg, setLogDlg] = useState(false);
  const [logRows, setLogRows] = useState([]);

  const load = async () => {
    try {
      const [v, c, s, tpl] = await Promise.all([
        api.get("/crm/visits"), api.get("/crm/checklist"), api.get("/crm/settings"),
        api.get("/crm/email-templates?kind=reco"),
      ]);
      setVisits(v.data); setChecklist(c.data); setSettings(s.data || {}); setRecoTemplates(tpl.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const openDir = (v) => {
    setTarget(v);
    setDirForm({ visit_date: v.visit_date || "", visit_time: v.visit_time || "", recommendations: "", attache_email: settings.attache_email || "" });
    setDirDlg(true);
  };
  const openFam = (v) => {
    setTarget(v);
    setFamForm({ visit_date: v.visit_date || "", visit_time: v.visit_time || "",
      address: settings.visit_address || settings.commune || "",
      cancel_phone: settings.cancel_phone || settings.contact_phone || "",
      cancel_email: settings.cancel_email || settings.contact_email || "" });
    setFamDlg(true);
  };
  const sendDir = async () => {
    if (!dirForm.attache_email.trim()) return toast.error("Renseignez votre adresse e-mail pro (pour l'accusé de réception).");
    setSendingDir(true);
    try {
      const r = await api.post(`/crm/visits/${target.id}/notify-direction`, dirForm);
      toast.success(`Direction prévenue (${r.data.sent} e-mail·s).`); setDirDlg(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSendingDir(false); }
  };
  const sendFam = async () => {
    setSendingFam(true);
    try {
      const r = await api.post(`/crm/visits/${target.id}/confirm-family`, famForm);
      toast.success(`Confirmation envoyée à ${r.data.to}.`); setFamDlg(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSendingFam(false); }
  };
  const confirmReception = async (v) => {
    try { await api.post(`/crm/visits/${v.id}/confirm-reception`); toast.success("Réception confirmée par la direction."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveRecoTemplate = async () => {
    if (!dirForm.recommendations.trim()) return toast.error("Écrivez d'abord des recommandations.");
    const label = window.prompt("Nom du modèle de recommandations :");
    if (!label) return;
    try { await api.post("/crm/email-templates", { kind: "reco", label, text: dirForm.recommendations }); toast.success("Modèle enregistré."); const t = await api.get("/crm/email-templates?kind=reco"); setRecoTemplates(t.data || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openLog = async (v) => {
    try { const r = await api.get(`/crm/email-log/visit/${v.id}`); setLogRows(r.data || []); setLogDlg(true); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const familyResponse = async (v, response) => {
    try { await api.post(`/crm/visits/${v.id}/family-response`, { response }); toast.success(response === "confirmee" ? "Visite marquée confirmée par la famille." : "Visite marquée annulée par la famille."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const activeChecklist = useMemo(() => checklist.filter((c) => c.active), [checklist]);

  const openNew = () => {
    setForm({ ...emptyVisit, checklist: activeChecklist.map((c) => ({ label: c.label, checked: false })) });
    setEdit(null); setOpen(true);
  };
  const openEdit = (v) => {
    setForm({ ...emptyVisit, ...v, checklist: (v.checklist || []).map((x) => ({ ...x })) });
    setEdit(v.id); setOpen(true);
  };

  const onProspect = (pid) => {
    const p = prospects.find((x) => x.id === pid);
    setForm((f) => ({ ...f, prospect_id: pid, prospect_name: p ? (`${p.last_name || ""} ${p.first_name || ""}`.trim() || p.contact_name || "") : f.prospect_name }));
  };
  const toggleItem = (idx, val) => setForm((f) => ({ ...f, checklist: f.checklist.map((c, i) => i === idx ? { ...c, checked: !!val } : c) }));

  const save = async () => {
    if (!form.prospect_id && !form.prospect_name.trim()) return toast.error(t("crm.visit.selectProspectErr"));
    try {
      if (edit) await api.put(`/crm/visits/${edit}`, form);
      else await api.post("/crm/visits", form);
      toast.success(t("crm.visit.saved")); setOpen(false); load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm(t("crm.visit.confirmDelete"))) return;
    await api.delete(`/crm/visits/${id}`); toast.success(t("crm.visit.deleted")); load(); onChange?.();
  };

  const addItem = async () => {
    if (!newItem.trim()) return;
    await api.post("/crm/checklist", { label: newItem.trim(), active: true });
    setNewItem(""); const c = await api.get("/crm/checklist"); setChecklist(c.data);
  };
  const updItem = async (it, patch) => {
    await api.put(`/crm/checklist/${it.id}`, { label: patch.label ?? it.label, active: patch.active ?? it.active });
    const c = await api.get("/crm/checklist"); setChecklist(c.data);
  };
  const delItem = async (id) => { await api.delete(`/crm/checklist/${id}`); const c = await api.get("/crm/checklist"); setChecklist(c.data); };
  const move = async (idx, dir) => {
    const arr = [...checklist]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]]; setChecklist(arr);
    await api.post("/crm/checklist/reorder", { ids: arr.map((x) => x.id) });
  };

  const doneCount = (v) => (v.checklist || []).filter((c) => c.checked).length;
  const stChip = (k) => { const d = CRM_VISIT_STATUS[k] || CRM_VISIT_STATUS.planifiee; return (
    <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium" style={{ color: d.text, background: d.bg }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: d.dot }} />{enumLabel(t, "visitStatus", k, CRM_VISIT_STATUS)}</span>); };

  return (
    <div className="space-y-4" data-testid="crm-visits-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2"><CalendarClock className="h-4 w-4 text-violet-400" /> {t("crm.visit.title")}</h3>
        <div className="flex gap-2">
          {!readOnly && <Button variant="outline" onClick={() => setClOpen(true)} data-testid="crm-manage-checklist"><ListChecks className="h-4 w-4 mr-2" /> {t("crm.visit.customize")}</Button>}
          {!readOnly && <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-500" data-testid="crm-add-visit"><Plus className="h-4 w-4 mr-2" /> {t("crm.visit.addBtn")}</Button>}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {visits.map((v) => (
          <div key={v.id} className="rounded-2xl border border-white/10 p-4 bg-white/[0.02]" data-testid={`crm-visit-${v.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-medium">{v.prospect_name || "—"}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Clock className="h-3 w-3" /> {v.visit_date || "—"} {v.visit_time}</div>
              </div>
              {stChip(v.status)}
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-foreground/70">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> {t("crm.visit.pointsValidated", { done: doneCount(v), total: (v.checklist || []).length })}
            </div>
            {v.next_step && <div className="text-xs text-foreground/60 mt-1 truncate">{t("crm.visit.nextStep")} : {v.next_step}</div>}
            {!readOnly && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => openDir(v)} data-testid={`visit-notify-direction-${v.id}`}><Building2 className="h-3.5 w-3.5" /> Prévenir la direction</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => openFam(v)} data-testid={`visit-confirm-family-${v.id}`}><Mail className="h-3.5 w-3.5" /> Confirmer à la famille</Button>
                {v.direction_notified_at && !v.direction_confirmed_at && (
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/10" onClick={() => confirmReception(v)} data-testid={`visit-confirm-reception-${v.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Réception confirmée ?</Button>
                )}
                <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-foreground/60" onClick={() => openLog(v)} data-testid={`visit-log-${v.id}`}><Clock className="h-3.5 w-3.5" /> Historique</Button>
                {v.family_confirmed_at && !v.family_response && (
                  <>
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/10" onClick={() => familyResponse(v, "confirmee")} data-testid={`visit-family-confirmed-${v.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Famille a confirmé</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-rose-300 border-rose-500/30 hover:bg-rose-500/10" onClick={() => familyResponse(v, "annulee")} data-testid={`visit-family-cancelled-${v.id}`}>Famille a annulé</Button>
                  </>
                )}
              </div>
            )}
            {(v.direction_notified_at || v.family_confirmed_at || v.family_response) && (
              <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
                {v.direction_notified_at && <span className={v.direction_confirmed_at ? "text-emerald-400" : "text-amber-400"}>{v.direction_confirmed_at ? "✓ direction confirmée" : "✓ direction prévenue (accusé en attente)"}</span>}
                {v.family_confirmed_at && <span className="text-sky-400">✓ famille confirmée</span>}
                {v.family_response === "confirmee" && <span className="text-emerald-400 font-semibold">✓ visite confirmée par la famille</span>}
                {v.family_response === "annulee" && <span className="text-rose-400 font-semibold">✕ visite annulée par la famille</span>}
              </div>
            )}
            <div className="mt-3 flex justify-end gap-1">
              {!readOnly && <><Button size="icon" variant="ghost" onClick={() => openEdit(v)} data-testid={`crm-edit-visit-${v.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="text-rose-400" onClick={() => del(v.id)}><Trash2 className="h-4 w-4" /></Button></>}
            </div>
          </div>
        ))}
        {!visits.length && <p className="text-muted-foreground text-sm col-span-full py-8 text-center">{t("crm.visit.empty")}</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("crm.visit.plan")}</DialogTitle>
            <DialogDescription className="sr-only">{t("crm.visit.title")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2"><Label>{t("crm.visit.prospect")}</Label>
                <Select value={form.prospect_id || "none"} onValueChange={(v) => onProspect(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="crm-visit-prospect"><SelectValue placeholder={t("crm.visit.selectProspect")} /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>
                    {prospects.map((p) => <SelectItem key={p.id} value={p.id}>{p.last_name} {p.first_name}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>{t("crm.visit.date")}</Label><Input type="date" value={form.visit_date || ""} onChange={(e) => setForm({ ...form, visit_date: e.target.value })} data-testid="crm-visit-date" /></div>
              <div className="space-y-1.5"><Label>{t("crm.visit.time")}</Label><Input type="time" value={form.visit_time || ""} onChange={(e) => setForm({ ...form, visit_time: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>{t("crm.visit.status")}</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="crm-visit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(CRM_VISIT_STATUS).map((k) => <SelectItem key={k} value={k}>{enumLabel(t, "visitStatus", k, CRM_VISIT_STATUS)}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>{t("crm.visit.conductedBy")}</Label><Input value={form.conducted_by || ""} onChange={(e) => setForm({ ...form, conducted_by: e.target.value })} /></div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-emerald-300 mb-2 flex items-center gap-1"><ListChecks className="h-3.5 w-3.5" /> {t("crm.visit.checklist")}</p>
              <div className="space-y-1.5 rounded-xl border border-white/10 p-3 bg-white/[0.02]">
                {form.checklist.map((c, i) => (
                  <label key={i} className="flex items-center gap-2 cursor-pointer text-sm py-0.5">
                    <Checkbox checked={c.checked} onCheckedChange={(v) => toggleItem(i, v)} data-testid={`crm-visit-check-${i}`} />
                    <span className={c.checked ? "line-through text-foreground/50" : ""}>{c.label}</span>
                  </label>
                ))}
                {!form.checklist.length && <p className="text-xs text-muted-foreground">{t("crm.visit.noActive")}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>{t("crm.visit.nextStep")}</Label><Input value={form.next_step || ""} onChange={(e) => setForm({ ...form, next_step: e.target.value })} placeholder={t("crm.visit.nextStepPlaceholder")} /></div>
              <div className="space-y-1.5"><Label>{t("crm.visit.nextFollowup")}</Label><Input type="date" value={form.next_followup || ""} onChange={(e) => setForm({ ...form, next_followup: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>{t("crm.visit.notesFrein")}</Label><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("crm.actions.cancel")}</Button>
            <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-500" data-testid="crm-save-visit">{t("crm.actions.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={clOpen} onOpenChange={setClOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("crm.visit.mgrTitle")}</DialogTitle>
            <DialogDescription>{t("crm.visit.mgrDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {checklist.map((it, i) => (
              <div key={it.id} className="flex items-center gap-2 rounded-lg border border-white/10 p-2 bg-white/[0.02]" data-testid={`crm-cl-item-${it.id}`}>
                <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
                <Input value={it.label} onChange={(e) => setChecklist(checklist.map((x) => x.id === it.id ? { ...x, label: e.target.value } : x))}
                  onBlur={(e) => updItem(it, { label: e.target.value })} className="h-8" />
                <Switch checked={it.active} onCheckedChange={(v) => updItem(it, { active: v })} data-testid={`crm-cl-active-${it.id}`} />
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(i, 1)} disabled={i === checklist.length - 1}><ArrowDown className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-400" onClick={() => delItem(it.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 pt-2">
            <Input value={newItem} onChange={(e) => setNewItem(e.target.value)} placeholder={t("crm.visit.newItem")} onKeyDown={(e) => e.key === "Enter" && addItem()} data-testid="crm-cl-new" />
            <Button onClick={addItem} className="bg-emerald-600 hover:bg-emerald-500" data-testid="crm-cl-add"><Plus className="h-4 w-4 mr-1" /> {t("crm.actions.add")}</Button>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setClOpen(false)}>{t("crm.actions.close")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dirDlg} onOpenChange={setDirDlg}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Building2 className="h-4 w-4 text-emerald-400" /> Prévenir la direction</DialogTitle>
            <DialogDescription>Un e-mail (no-reply) sera envoyé au directeur et à l'adjoint de la maison, avec accusé de réception vers votre adresse pro.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Date de la visite</Label><Input type="date" value={dirForm.visit_date} onChange={(e) => setDirForm({ ...dirForm, visit_date: e.target.value })} data-testid="dir-visit-date" /></div>
              <div className="space-y-1.5"><Label>Heure</Label><Input type="time" value={dirForm.visit_time} onChange={(e) => setDirForm({ ...dirForm, visit_time: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label>Recommandations (chambres prêtes, accueil…)</Label>
                <Button type="button" size="sm" variant="ghost" className="h-6 text-[11px] gap-1 text-sky-300" onClick={saveRecoTemplate} data-testid="dir-save-template">+ Enregistrer comme modèle</Button>
              </div>
              {recoTemplates.length > 0 && (
                <Select value="" onValueChange={(id) => { const tp = recoTemplates.find((x) => x.id === id); if (tp) setDirForm({ ...dirForm, recommendations: tp.text }); }}>
                  <SelectTrigger className="h-8 text-xs" data-testid="dir-template-picker"><SelectValue placeholder="Insérer un modèle enregistré…" /></SelectTrigger>
                  <SelectContent>{recoTemplates.map((tp) => <SelectItem key={tp.id} value={tp.id}>{tp.label}</SelectItem>)}</SelectContent>
                </Select>
              )}
              <Textarea rows={4} value={dirForm.recommendations} onChange={(e) => setDirForm({ ...dirForm, recommendations: e.target.value })} placeholder="Ex. Merci de préparer la chambre 12, prévoir un accueil personnalisé…" data-testid="dir-reco" />
            </div>
            <div className="space-y-1.5"><Label>Votre adresse e-mail pro (accusé de réception)</Label><Input type="email" value={dirForm.attache_email} onChange={(e) => setDirForm({ ...dirForm, attache_email: e.target.value })} placeholder="prenom.nom@novadia.be" data-testid="dir-attache-email" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDirDlg(false)}>{t("crm.actions.cancel")}</Button>
            <Button onClick={sendDir} disabled={sendingDir} className="bg-emerald-600 hover:bg-emerald-500 gap-1.5" data-testid="dir-send"><Send className="h-4 w-4" /> {sendingDir ? "Envoi…" : "Envoyer à la direction"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={famDlg} onOpenChange={setFamDlg}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="h-4 w-4 text-sky-400" /> Confirmer la visite à la famille</DialogTitle>
            <DialogDescription>Un e-mail de confirmation sera envoyé à la famille (date, heure, adresse) avec les modalités d'annulation/confirmation.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Date de la visite</Label><Input type="date" value={famForm.visit_date} onChange={(e) => setFamForm({ ...famForm, visit_date: e.target.value })} data-testid="fam-visit-date" /></div>
              <div className="space-y-1.5"><Label>Heure</Label><Input type="time" value={famForm.visit_time} onChange={(e) => setFamForm({ ...famForm, visit_time: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Adresse de la maison</Label><Input value={famForm.address} onChange={(e) => setFamForm({ ...famForm, address: e.target.value })} placeholder="Rue, numéro, code postal, commune" data-testid="fam-address" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Téléphone (annuler/confirmer)</Label><Input value={famForm.cancel_phone} onChange={(e) => setFamForm({ ...famForm, cancel_phone: e.target.value })} placeholder="+32 …" data-testid="fam-phone" /></div>
              <div className="space-y-1.5"><Label>E-mail (annuler/confirmer)</Label><Input type="email" value={famForm.cancel_email} onChange={(e) => setFamForm({ ...famForm, cancel_email: e.target.value })} placeholder="contact@novadia.be" data-testid="fam-email" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFamDlg(false)}>{t("crm.actions.cancel")}</Button>
            <Button onClick={sendFam} disabled={sendingFam} className="bg-sky-600 hover:bg-sky-500 gap-1.5" data-testid="fam-send"><Send className="h-4 w-4" /> {sendingFam ? "Envoi…" : "Envoyer à la famille"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={logDlg} onOpenChange={setLogDlg}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Clock className="h-4 w-4 text-violet-400" /> Historique des e-mails</DialogTitle>
            <DialogDescription>Tous les e-mails envoyés pour cette visite.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2 max-h-[50vh] overflow-y-auto" data-testid="visit-log-list">
            {logRows.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">Aucun e-mail envoyé pour le moment.</p>}
            {logRows.map((l) => (
              <div key={l.id} className="rounded-xl border border-white/10 p-3 bg-white/[0.02]">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold capitalize">{l.kind === "direction" ? "Direction" : l.kind === "famille" ? "Famille" : l.kind}</span>
                  <span className="text-[11px] text-muted-foreground">{l.sent_at ? new Date(l.sent_at).toLocaleString("fr-BE") : ""}</span>
                </div>
                <div className="text-xs text-foreground/70 mt-1 truncate">À : {l.to}</div>
                {l.sent_by && <div className="text-[11px] text-muted-foreground">Par {l.sent_by}</div>}
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setLogDlg(false)}>{t("crm.actions.close")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
